import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

const ARTICLE_TICKERS = new Set([
  'ACMR', 'MXL', 'PENG', 'EXTR', 'RBRK', 'VRNS', 'ALGM', 'ALAB', 'FLEX', 'HPE', 'LEU', 'STRL',
  'APLD', 'AMBA', 'SMR', 'OKLO', 'MSFT', 'META', 'NVDA', 'AMD', 'AMAT', 'LRCX', 'ANET', 'CSCO',
  'CEG', 'VST', 'CRWD', 'PANW', 'DELL', 'SMCI', 'AMZN', 'MU', 'AVGO', 'ETN', 'VRT', 'FLR', 'PWR',
  'SPY', 'SMH', 'XLK', 'XLP', 'XLE', 'XLU', 'XHB', 'IYR', 'USO', 'GLD', 'TSLA', 'GOOGL',
  'BWXT', 'UEC',
  'DHI', 'LEN', 'PHM', 'KBH', 'MTH', 'NVR', 'TOL', 'SKY', 'RKT', 'UWMC', 'PFSI', 'WD',
  'NLY', 'AGNC', 'BXMT', 'RITM', 'STWD', 'WMT', 'COST', 'TGT', 'DG', 'BJ', 'CPB', 'GIS', 'CAG', 'MKC', 'PG',
  'NEE', 'DUK', 'SO', 'AEP', 'EXC', 'WEC', 'CMS', 'NEM', 'AEM', 'RGLD', 'FNV', 'WPM', 'GOLD',
  'XOM', 'CVX', 'COP', 'DVN', 'FANG', 'EOG', 'OXY', 'APA',
  'TENB', 'ZS', 'QLYS', 'OKTA', 'LASR', 'NTAP', 'SMTC', 'VSAT', 'TPC',
]);

const TICKER_PATTERN = /\b([A-Z]{1,5})\b/;

function renderInlineWithTickers(text: string, keyPrefix: string, isSubstack = false) {
  const parts = text.split(TICKER_PATTERN);
  return parts.map((part, index) => {
    if (index % 2 === 1 && ARTICLE_TICKERS.has(part)) {
      return (
        <Link
          key={`${keyPrefix}-${index}`}
          to={`/stock/${part}`}
          className={
            isSubstack
              ? 'substack-a'
              : 'font-medium text-violet-700 underline decoration-violet-200 underline-offset-2 hover:text-violet-900'
          }
        >
          {part}
        </Link>
      );
    }
    return <span key={`${keyPrefix}-${index}`}>{part}</span>;
  });
}

function collectText(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(collectText).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    const element = children as { props?: { children?: React.ReactNode } };
    return collectText(element.props?.children ?? '');
  }
  return '';
}

type Props = {
  markdown: string;
  variant?: 'default' | 'substack';
};

export function ArticleMarkdown({ markdown, variant = 'default' }: Props) {
  const isSubstack = variant === 'substack';

  const components = useMemo<Components>(
    () => ({
      h1: ({ children }) => (
        <h1 className={isSubstack ? 'substack-h1' : 'mb-6 text-3xl tracking-tight text-gray-900 md:text-4xl'}>
          {children}
        </h1>
      ),
      h2: ({ children }) => (
        <h2
          className={
            isSubstack
              ? 'substack-h2'
              : 'mb-4 mt-12 border-b border-gray-200 pb-2 text-2xl tracking-tight text-gray-900'
          }
        >
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className={isSubstack ? 'substack-h3' : 'mb-3 mt-8 text-xl font-semibold text-gray-900'}>
          {children}
        </h3>
      ),
      h4: ({ children }) => (
        <h4 className={isSubstack ? 'substack-h4' : 'mb-2 mt-6 text-lg font-semibold text-gray-900'}>
          {children}
        </h4>
      ),
      p: ({ children }) => (
        <p className={isSubstack ? 'substack-p' : 'mb-4 text-[15px] leading-7 text-gray-700'}>
          {renderInlineWithTickers(collectText(children), 'p', isSubstack)}
        </p>
      ),
      ul: ({ children }) => (
        <ul className={isSubstack ? 'substack-ul' : 'mb-5 list-disc space-y-2 pl-6 text-[15px] leading-7 text-gray-700'}>
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol className={isSubstack ? 'substack-ol' : 'mb-5 list-decimal space-y-2 pl-6 text-[15px] leading-7 text-gray-700'}>
          {children}
        </ol>
      ),
      li: ({ children }) => (
        <li className={isSubstack ? 'substack-li' : undefined}>
          {renderInlineWithTickers(collectText(children), 'li', isSubstack)}
        </li>
      ),
      strong: ({ children }) => (
        <strong className={isSubstack ? 'substack-strong' : 'font-semibold text-gray-900'}>{children}</strong>
      ),
      em: ({ children }) => (
        <em className={isSubstack ? 'substack-em' : 'italic text-gray-700'}>{children}</em>
      ),
      hr: () => <hr className={isSubstack ? 'substack-hr' : 'my-10 border-gray-200'} />,
      blockquote: ({ children }) => (
        <blockquote
          className={
            isSubstack
              ? 'substack-blockquote'
              : 'my-6 border-l-4 border-violet-300 bg-violet-50/50 px-4 py-3 text-gray-700'
          }
        >
          {children}
        </blockquote>
      ),
      a: ({ href, children }) => (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className={
            isSubstack
              ? 'substack-a'
              : 'font-medium text-violet-700 underline decoration-violet-200 underline-offset-2 hover:text-violet-900'
          }
        >
          {children}
        </a>
      ),
      code: ({ children, className }) => {
        const isBlock = className?.includes('language-');
        if (isBlock) {
          return (
            <code className="block overflow-x-auto rounded-xl bg-gray-900 px-4 py-3 text-sm text-gray-100">
              {children}
            </code>
          );
        }
        return (
          <code className="rounded bg-gray-100 px-1.5 py-0.5 text-sm text-gray-800">{children}</code>
        );
      },
      pre: ({ children }) => (
        <pre className="mb-5 overflow-x-auto rounded-xl bg-gray-900 p-4 text-sm text-gray-100">{children}</pre>
      ),
      table: ({ children }) => (
        <div className="mb-6 overflow-x-auto rounded-2xl border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">{children}</table>
        </div>
      ),
      thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
      tbody: ({ children }) => <tbody className="divide-y divide-gray-100 bg-white">{children}</tbody>,
      tr: ({ children }) => <tr>{children}</tr>,
      th: ({ children }) => (
        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="px-4 py-3 align-top text-gray-700">
          {renderInlineWithTickers(collectText(children), 'td', isSubstack)}
        </td>
      ),
    }),
    [isSubstack],
  );

  return (
    <article className={isSubstack ? 'article-markdown substack-markdown' : 'article-markdown max-w-3xl'}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {markdown}
      </ReactMarkdown>
    </article>
  );
}
